import { HINT } from "@/components/tool-kit/styles";

/*
 * A thin progress bar for a count against a limit. The numbers beside it are text in a
 * live region owned by the caller; the bar itself is decoration, hidden from assistive
 * technology so a screen reader hears the sentence once, not a percentage as well.
 */

export type MeterTone = "ok" | "near" | "over" | "idle";

const TONES: Record<MeterTone, string> = {
  ok: "bg-primary",
  near: "bg-amber-500",
  over: "bg-destructive",
  idle: "bg-muted-foreground/40",
};

/** `near` from 90% of the limit, `over` above it, `idle` while empty. */
export function toneFor(value: number, limit: number): MeterTone {
  if (value <= 0) {
    return "idle";
  }
  if (value > limit) {
    return "over";
  }
  return value >= limit * 0.9 ? "near" : "ok";
}

export function MeterBar({
  value,
  limit,
  tone,
  marker,
}: {
  value: number;
  limit: number;
  tone: MeterTone;
  /** A tick on the bar, as a count — a minimum the writer must pass. */
  marker?: number;
}) {
  const width = Math.min(100, limit > 0 ? (value / limit) * 100 : 0);
  const tick =
    marker !== undefined && limit > 0
      ? Math.min(100, (marker / limit) * 100)
      : null;
  return (
    <div
      aria-hidden="true"
      className="relative h-2 w-full overflow-hidden rounded-full bg-muted"
    >
      <div
        className={`h-full rounded-full transition-[width] ${TONES[tone]}`}
        style={{ width: `${width}%` }}
      />
      {tick === null ? null : (
        <div
          className="absolute top-0 h-full w-0.5 bg-foreground/60"
          style={{ left: `${tick}%` }}
        />
      )}
    </div>
  );
}

/** A bar with a caption line under it. */
export function Meter({
  value,
  limit,
  tone,
  marker,
  caption,
}: {
  value: number;
  limit: number;
  tone: MeterTone;
  marker?: number;
  caption: string;
}) {
  return (
    <div className="space-y-1">
      <MeterBar limit={limit} marker={marker} tone={tone} value={value} />
      <p aria-live="polite" className={HINT}>
        {caption}
      </p>
    </div>
  );
}
